import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, routeForUser } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { KeyRound, Loader2, UserPlus } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth(role) {
  // We can't send headers with a redirect, so we pass role as a fragment param.
  // AuthCallback will read it and forward to backend.
  const redirectUrl =
    window.location.origin + `/auth/callback#role=${encodeURIComponent(role)}`;
  // Encode the role-in-fragment as part of the redirect target.
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Signup() {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [role, setRole] = useState("student");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [parentMobile, setParentMobile] = useState("");
  const [loading, setLoading] = useState(false);

  // Compute learner age from DOB. Returns null when DOB is not yet a valid date.
  const learnerAge = (() => {
    if (!dob) return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let a = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
    return a;
  })();
  const isUnder18 = role === "student" && learnerAge != null && learnerAge < 18;

  const submit = async (e) => {
    e.preventDefault();
    if (role === "student" && !dob) {
      toast.error("Date of birth is required for students");
      return;
    }
    if (isUnder18) {
      if (!parentEmail.trim() || !parentMobile.trim()) {
        toast.error("Under-18 signup needs a parent email + mobile.");
        return;
      }
      // Loose E.164: 8-15 digits, optional leading +.
      const cleaned = parentMobile.replace(/[\s-]/g, "");
      const digitsOk = /^\+?\d{8,15}$/.test(cleaned);
      if (!digitsOk) {
        toast.error("Enter a valid parent mobile number (e.g. +919999888877).");
        return;
      }
    }
    setLoading(true);
    try {
      const payload = { email, password, name, role };
      if (dob) payload.date_of_birth = dob;
      if (isUnder18) {
        payload.parent_email = parentEmail.trim();
        payload.parent_mobile = parentMobile.replace(/[\s-]/g, "");
      }
      const { data } = await api.post("/auth/register", payload);
      // status is always "pending" after register → no access_token returned.
      setAuth(data.access_token, data.user);
      const successMsg = isUnder18
        ? "Account created — invite sent to your parent. Waiting for admin approval."
        : "Account created — awaiting admin approval";
      toast.success(successMsg);
      navigate(routeForUser(data.user), { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Sign up failed";
      const map = {
        email_already_registered: "An account with this email already exists.",
        parent_contact_required: "Under-18 signup requires parent email + mobile.",
        parent_mobile_invalid: "That parent mobile number doesn't look valid.",
      };
      const msg = map[detail] || (typeof detail === "string" ? detail : "Sign up failed");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 flex flex-col" data-testid="signup-page">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-amber-500 text-stone-900 flex items-center justify-center">
              <KeyRound className="h-5 w-5" />
            </div>
            <div className="text-sm font-semibold tracking-tight">KKS</div>
          </Link>
          <Link to="/login" className="text-sm text-stone-600 hover:text-stone-900">
            Already a member? <span className="underline">Log in</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <Card className="w-full max-w-md border-stone-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <UserPlus className="h-4 w-4 text-amber-600" /> Create your KKS account
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={submit}>
              <div className="space-y-2">
                <Label>I am a…</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="signup-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student" data-testid="signup-role-student">Student</SelectItem>
                    <SelectItem value="teacher" data-testid="signup-role-teacher">Teacher</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Full name</Label>
                <Input
                  id="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="signup-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="signup-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password (min 8 chars)</Label>
                <Input
                  id="password"
                  type="password"
                  minLength={8}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="signup-password-input"
                />
              </div>

              {role === "student" && (
                <div className="space-y-2" data-testid="signup-dob-field">
                  <Label htmlFor="dob">Date of birth</Label>
                  <Input
                    id="dob"
                    type="date"
                    required
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    data-testid="signup-dob-input"
                  />
                  <p className="text-xs text-stone-500">
                    We tailor content to your age. Kept private.
                  </p>
                </div>
              )}

              {isUnder18 && (
                <div
                  className="space-y-3 rounded-md border border-amber-200 bg-amber-50/60 p-3"
                  data-testid="signup-parent-block"
                >
                  <div className="text-xs font-semibold text-amber-900">
                    Parent / guardian contact
                  </div>
                  <p className="text-xs text-stone-600">
                    Since you're under 18, we'll invite your parent as your
                    guardian on KKS. They can review your courses and approve
                    them.
                  </p>
                  <div className="space-y-1.5">
                    <Label htmlFor="parent-email">Parent email</Label>
                    <Input
                      id="parent-email"
                      type="email"
                      required
                      value={parentEmail}
                      onChange={(e) => setParentEmail(e.target.value)}
                      placeholder="parent@example.com"
                      data-testid="signup-parent-email-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="parent-mobile">Parent mobile</Label>
                    <Input
                      id="parent-mobile"
                      type="tel"
                      required
                      value={parentMobile}
                      onChange={(e) => setParentMobile(e.target.value)}
                      placeholder="+91 99998 88877"
                      data-testid="signup-parent-mobile-input"
                    />
                    <p className="text-xs text-stone-500">
                      Include country code (e.g. +91, +1).
                    </p>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-stone-900 hover:bg-stone-800 text-white"
                data-testid="signup-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating…
                  </>
                ) : (
                  "Create account"
                )}
              </Button>
            </form>

            <div className="my-5 flex items-center gap-3 text-xs text-stone-500">
              <div className="h-px flex-1 bg-stone-200" />
              or
              <div className="h-px flex-1 bg-stone-200" />
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => startGoogleAuth(role)}
              data-testid="signup-google-button"
            >
              Sign up with Google as {role}
            </Button>
            <p className="mt-3 text-xs text-stone-500">
              New accounts (email or Google) are reviewed by an admin before
              you can access your dashboard.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
